(() => {
  const instances = new WeakMap();
  function init(root) {
    if (instances.has(root)) return;
    const cleanup = [];
    const on = (target, event, handler) => { target.addEventListener(event, handler); cleanup.push(() => target.removeEventListener(event, handler)); };
    const staticPreviews = root.dataset.staticPreviews === 'true';
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection;
    const autoAllowed = () => !staticPreviews && !motion.matches && !connection?.saveData && !/^(slow-2g|2g)$/.test(connection?.effectiveType || '') && !window.Shopify?.designMode;
    const collection = root.querySelector('.pps-reels-track');
    const cards = [...root.querySelectorAll('.pps-reel')];
    if (root.dataset.shuffle === 'true' && !window.Shopify?.designMode && cards.length > 1) {
      for (let index = cards.length - 1; index > 0; index--) {
        const other = Math.floor(Math.random() * (index + 1));
        [cards[index], cards[other]] = [cards[other], cards[index]];
      }
      const order = () => cards.map(card => card.dataset.clipId).join(',');
      try {
        const key = 'pps-reels-order:' + collection.id;
        if (sessionStorage.getItem(key) === order()) cards.push(cards.shift());
        sessionStorage.setItem(key, order());
      } catch { /* Shuffling still works when browser storage is unavailable. */ }
      cards.forEach((card,index) => {
        collection.appendChild(card);
        const counter = card.querySelector('.pps-reel-counter');
        if (counter) counter.textContent = String(index + 1).padStart(2, '0');
      });
      collection.scrollLeft = 0;
    }
    const videos = [...root.querySelectorAll('video')];
    const states = new Map();
    const desktop = window.matchMedia('(min-width: 750px)');
    let activeVideo = null;
    let userPaused = false;
    const safePlay = video => { const result = video.play(); if (result?.catch) result.catch(() => {}); };
    const activate = (video, manual = false) => {
      videos.forEach(other => { if (other !== video) { other.pause(); other.muted = true; } });
      activeVideo = video;
      if (manual) { userPaused = false; states.get(video).manualPlay = true; }
      if (video.ended) video.currentTime = 0;
      safePlay(video);
    };
    videos.forEach(video => {
      const card = video.closest('.pps-reel');
      const play = card.querySelector('.pps-reel-play');
      const sound = card.querySelector('.pps-reel-sound');
      if (card.dataset?.silent === 'true') sound.hidden = true;
      const poster = card.querySelector('.pps-reel-poster');
      if (poster) video.poster = poster.content.textContent.trim();
      video.controls = false;
      video.setAttribute('playsinline', '');
      video.muted = true;
      video.loop = !desktop.matches;
      card.querySelector('.pps-reel-controls').hidden = staticPreviews;
      const state = { visible: false, manualPause: false, manualPlay: false };
      states.set(video, state);
      const sync = () => {
        play.setAttribute('aria-label', video.paused ? root.dataset.play : root.dataset.pause);
        play.querySelector('span').innerHTML = `
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    style="display:block"
  >
    ${video.paused
      ? '<path d="M8 5v14l11-7z"/>'
      : '<rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/>'
    }
  </svg>
`;
        sound.setAttribute('aria-label', video.muted ? root.dataset.soundOn : root.dataset.soundOff);
        sound.setAttribute('aria-pressed', String(!video.muted));
        sound.querySelector('.pps-reel-sound-state').textContent = video.muted ? 'OFF' : 'ON';
      };
      on(play, 'click', () => {
        if (video.paused) { state.manualPause = false; activate(video, true); }
        else { state.manualPause = true; state.manualPlay = false; userPaused = true; video.pause(); }
      });
      on(sound, 'click', () => {
        if (video.muted) videos.forEach(other => { if (other !== video) other.muted = true; });
        video.muted = !video.muted;
        sync();
      });
      ['play', 'pause', 'volumechange', 'ended'].forEach(event => on(video, event, sync));
      on(video, 'ended', () => {
        if (!desktop.matches || userPaused || document.hidden || !autoAllowed() || activeVideo !== video || root.contains(document.activeElement)) return;
        const index = videos.indexOf(video);
        for (let offset = 1; offset <= videos.length; offset++) {
          const nextVideo = videos[(index + offset) % videos.length];
          if (states.get(nextVideo).visible) { nextVideo.currentTime = 0; activate(nextVideo); break; }
        }
      });
      on(video, 'error', () => { video.controls = true; card.querySelector('.pps-reel-controls').hidden = true; });
      sync();
    });
    const resume = () => {
      videos.forEach(video => { if (!states.get(video).visible || document.hidden) video.pause(); });
      if (document.hidden || userPaused) return;
      const candidate = activeVideo && states.get(activeVideo).visible ? activeVideo : videos.find(video => states.get(video).visible);
      if (candidate && (states.get(candidate).manualPlay || autoAllowed())) activate(candidate);
      else videos.forEach(video => video.pause());
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => { const state = states.get(entry.target); state.visible = entry.isIntersecting && entry.intersectionRatio >= (desktop.matches ? .35 : .6); });
        resume();
      }, { threshold: [0, .35, .6] });
      videos.forEach(video => observer.observe(video));
      cleanup.push(() => observer.disconnect());
    }
    on(document, 'visibilitychange', resume);
    on(motion, 'change', resume);
    on(desktop, 'change', () => { videos.forEach(video => { video.loop = !desktop.matches; }); resume(); });
    if (connection?.addEventListener) on(connection, 'change', resume);
    const track = root.querySelector('.pps-reels-track');
    const nav = root.querySelector('.pps-reels-nav');
    const prev = root.querySelector('[data-reel-prev]');
    const next = root.querySelector('[data-reel-next]');
    const update = () => { const overflow = track.scrollWidth - track.clientWidth; nav.hidden = overflow <= 8; prev.disabled = track.scrollLeft < 5; next.disabled = track.scrollLeft >= overflow - 5; };
    const step = direction => { const card = track.querySelector('.pps-reel'); if (card) track.scrollBy({ left: direction * (card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap)), behavior: motion.matches ? 'instant' : 'smooth' }); };
    on(prev, 'click', () => step(-1));
    on(next, 'click', () => step(1));
    on(track, 'scroll', update);
    on(window, 'resize', update);
    if ('ResizeObserver' in window) {
      const resize = new ResizeObserver(update);
      resize.observe(track);
      cleanup.push(() => resize.disconnect());
    }
    update();
    instances.set(root, () => { cleanup.forEach(fn => fn()); videos.forEach(video => video.pause()); instances.delete(root); });
  }
  const scan = target => { if (target.matches?.('[data-pps-reels]')) init(target); target.querySelectorAll('[data-pps-reels]').forEach(init); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-pps-reels]').forEach(root => instances.get(root)?.()));
})();
